import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Form, InputGroup, Button } from "react-bootstrap";
import TopImage from "../../common/TopImage";
import { Images } from "../../../../config/images";
import SchoolTable from "./SchoolTable";
import { DashboardStats } from "../../../common/Dashboard/DashboardStats";
import DashboardStatsSkeleton from "../../../common/Dashboard/DashboardStatsSkeleton";
import { useGetSubSchoolReport } from "../../../../hooks/organization/report/useGetSubSchoolReport";
import { ITEMS_PER_PAGE, SEARCH_DEBOUNCE_TIME } from "../../../../constant/constant";
import RefreshButtonTable from "../../../common/RefreshButtonTable";
import "../../../common/Dashboard/Dashboard.css";
import "./School.css";

const School = () => {
  const { loading, error, reportData, getSubSchoolReport, metaData } =
    useGetSubSchoolReport();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimeoutRef = useRef(null);

  /**
   * Fetch sub-school report with current page and search
   */
  const fetchSchools = useCallback(
    (page, search) => {
      getSubSchoolReport(page, ITEMS_PER_PAGE, search.trim());
    },
    [getSubSchoolReport],
  );

  // Fetch report data on initial load and when page or search changes
  useEffect(() => {
    fetchSchools(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch]);

  /**
   * Handle search input change with debounce
   */
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      setDebouncedSearch(value);
    }, SEARCH_DEBOUNCE_TIME);
  };

  /**
   * Clear search input and reset results
   */
  const handleClearSearch = () => {
    setSearchTerm("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setCurrentPage(1);
    setDebouncedSearch("");
  };

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    fetchSchools(currentPage, debouncedSearch);
  };

  const isSearching = debouncedSearch.trim() !== "";

  const stats = [
    {
      label: "Total Schools",
      value:
        loading && !reportData
          ? "..."
          : (reportData?.stats?.total_schools ?? 0),
      icon: "fa-graduation-cap",
      colorClass: "dashboard-stat-card-purple",
    },
    {
      label: "Total Students",
      value:
        loading && !reportData
          ? "..."
          : (reportData?.stats?.total_students ?? 0).toLocaleString(),
      icon: "fa-user",
      colorClass: "dashboard-stat-card-green",
    },
    {
      label: "Avg. Completion Rate",
      value:
        loading && !reportData
          ? "..."
          : `${reportData?.stats?.avg_completion_rate ?? 0}%`,
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-peach",
    },
    {
      label: "Avg. Quiz Accuracy",
      value:
        loading && !reportData
          ? "..."
          : `${reportData?.stats?.avg_quiz_accuracy ?? 0}%`,
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-pastel-blue",
    },
  ];

  return (
    <>
      <div className="container-fluid p-0">
        <div className="p-0">
          <section>
            <div>
              <TopImage
                imageSrc={Images.TopImage}
                text="School Performance Overview"
              />

              {/* Stats Cards */}
              <div className="pt-4">
                {loading && !reportData ? (
                  <DashboardStatsSkeleton count={4} />
                ) : (
                  <DashboardStats stats={stats} />
                )}
              </div>

              {/* Action Buttons */}
              <div className="dashboard-section-header mt-4">
                <h2 className="dashboard-section-title">SCHOOLS OVERVIEW</h2>
                <div className="dashboard-header-actions">
                  <Link
                    to="/organization/school/add-school"
                    className="dashboard-action-btn"
                  >
                    + Add School
                  </Link>
                  <Link
                    to="/organization/license/assign"
                    className="dashboard-action-btn"
                  >
                    + Assign Licenses
                  </Link>
                </div>
              </div>

            </div>
          </section>

          {/* Schools Table */}
          <div className="mt-4">
            {/* Search and Refresh Row */}
            <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
              <InputGroup style={{ maxWidth: "400px" }}>
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by school name..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  disabled={loading && !isSearching && reportData?.schools?.data === undefined}
                  className="shadow-none"
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={handleClearSearch}
                    disabled={loading}
                    title="Clear search"
                  >
                    <i className="bi bi-x-lg"></i>
                  </Button>
                )}
              </InputGroup>

              <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
            </div>

            <SchoolTable
              schools={reportData?.schools?.data}
              loading={loading}
              error={error}
              metaData={metaData}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              handleRefresh={handleRefresh}
              isSearching={isSearching}
              debouncedSearch={debouncedSearch}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default School;
